import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { fetchMe, logout as apiLogout, applyReferralCode } from "@/lib/api";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// Capture ?ref=CODE on any page load and stash it for after sign-in.
function captureRefFromUrl() {
  try {
    const url = new URL(window.location.href);
    const ref = url.searchParams.get("ref");
    if (ref && /^[A-Z0-9]{8}$/i.test(ref)) {
      window.localStorage.setItem("reviseai_ref", ref.toUpperCase());
    }
  } catch (e) {
    console.warn("[auth] captureRefFromUrl failed:", e);
  }
}
captureRefFromUrl();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const me = await fetchMe();
      setUser(me);
      // Best-effort: apply a referral code captured before sign-in.
      const stashed = window.localStorage.getItem("reviseai_ref");
      if (stashed && me) {
        const canApply = !me.referred_by_user_id
          && me.subscription_status !== "active"
          && me.subscription_status !== "trialing";
        if (canApply) {
          try {
            await applyReferralCode(stashed);
          } catch (e) {
            // Invalid / already-applied / already-subscribed — expected; log at debug only.
            console.debug("[auth] referral apply skipped:", e?.response?.data?.detail || e?.message);
          }
        }
        // Always clear: either we applied it, or the user can no longer use it.
        window.localStorage.removeItem("reviseai_ref");
      }
    } catch (e) {
      // 401 unauthenticated is the normal anonymous state; only warn on other errors.
      if (e?.response?.status && e.response.status !== 401) {
        console.warn("[auth] fetchMe failed:", e.response.status, e.response.data);
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const signOut = async () => {
    try {
      await apiLogout();
    } catch (e) {
      console.debug("[auth] logout request failed:", e?.message);
    }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, refresh, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}
